export default defineAppConfig({
  featureFlags: {
    agentInbox: true,
  } as Record<string, boolean>,

  ui: {
    colors: {
      primary: 'bamboo',
      neutral: 'neutral',
    },

    tree: {
      slots: {
        listWithChildren: 'ml-1.5 pl-1.5 border-l border-neutral-200 dark:border-neutral-800',
      },
    },
    dashboardSidebar: {
      slots: {
        header: 'h-(--ui-header-height) shrink-0 flex items-center gap-1.5 px-4 !bg-red/40',
      },
    },
    dashboardPanel: {
      slots: {
        root: 'h-full flex-1 min-h-0',
      },
    },
    dashboardNavbar: {
      slots: {
        root: '!h-(--ui-header-height) shrink-0 flex items-center justify-between border-none px-4 sm:px-6 gap-1 !bg-muted',
        left: 'flex items-center gap-1.5 min-w-0',
        icon: 'shrink-0 size-5 self-center me-1.5',
        title: 'flex items-center gap-1.5 font-semibold text-highlighted truncate',
        center: 'hidden lg:flex',
        right: 'flex items-center shrink-0 gap-1.5',
        toggle: '',
      },
    },
    table: {
      slots: {
        root: 'relative overflow-auto',
        base: 'min-w-full overflow-clip border border-muted bg-elevated/40',
        thead: 'relative border-b border-default',
        tbody: 'divide-y divide-default',
        th: 'px-4 py-2 text-xs text-left rtl:text-right text-muted font-medium bg-inverted/10 [&:has([role=checkbox])]:pe-0',
        td: 'px-4 py-2.5 text-sm text-muted whitespace-nowrap empty:p-0 [&:has([role=checkbox])]:pe-0',
      },
      variants: {
        variant: {
          data: {},
          list: {
            base: 'min-w-full table-fixed overflow-clip border border-muted bg-elevated/40',
            th: 'px-3 py-1 text-xs text-left font-medium text-muted',
            td: 'px-3 py-2 align-top text-sm text-muted empty:p-0',
          },
          flush: {
            root: 'relative overflow-auto h-full flex-1 min-h-0',
            base: 'min-w-full table-fixed bg-default',
            th: 'px-3 py-2 text-xs text-left font-medium text-muted',
          },
        },
      },
    },

    editor: {
      slots: {
        root: 'outline-none ring-0 ring-transparent focus-within:outline-none focus-within:ring-0',
        content: 'outline-none focus:outline-none focus-visible:outline-none',
        base: [
          // Content formatting (Nuxt UI Editor default base – required for headings, paragraphs, links, etc.)
          'w-full outline-none *:my-5 *:first:mt-0 *:last:mb-0 sm:px-8 selection:bg-primary/20',
          '[&_p]:leading-7',
          '[&_a]:text-primary [&_a]:border-b [&_a]:border-transparent [&_a]:hover:border-primary [&_a]:font-medium [&_a]:transition-colors',
          '[&_a>code]:border-dashed [&_a:hover>code]:border-primary [&_a:hover>code]:text-primary [&_a>code]:transition-colors',
          '[&_.mention]:text-primary [&_.mention]:font-medium',
          '[&_:is(h1,h2,h3,h4,h5,h6)]:text-highlighted [&_:is(h1,h2,h3,h4,h5,h6)]:font-bold',
          '[&_h1]:text-3xl [&_h2]:text-2xl [&_h3]:text-xl [&_h4]:text-lg [&_h5]:text-base [&_h6]:text-base',
          '[&_:is(h1,h2,h3,h4,h5,h6)>code]:border-dashed [&_:is(h1,h2,h3,h4,h5,h6)>code]:font-bold [&_h2>code]:text-xl/6 [&_h3>code]:text-lg/5',
          '[&_blockquote]:border-s-4 [&_blockquote]:border-accented [&_blockquote]:ps-4 [&_blockquote]:italic',
          '[&_[data-type=horizontalRule]]:my-8 [&_[data-type=horizontalRule]]:py-2 [&_hr]:border-t [&_hr]:border-default',
          '[&_pre]:text-sm/6 [&_pre]:border [&_pre]:border-muted [&_pre]:bg-muted [&_pre]:rounded-md [&_pre]:px-4 [&_pre]:py-3 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:overflow-x-auto',
          '[&_pre_code]:p-0 [&_pre_code]:text-inherit [&_pre_code]:font-inherit [&_pre_code]:rounded-none [&_pre_code]:inline [&_pre_code]:border-0 [&_pre_code]:bg-transparent',
          '[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono [&_code]:font-medium [&_code]:rounded-md [&_code]:inline-block [&_code]:border [&_code]:border-muted [&_code]:text-highlighted [&_code]:bg-muted',
          '[&_:is(ul,ol)]:ps-6 [&_ul]:list-disc [&_ul]:marker:text-(--ui-border-accented) [&_ol]:list-decimal [&_ol]:marker:text-muted [&_li]:my-1.5 [&_li]:ps-1.5',
          '[&_img]:rounded-md [&_img]:block [&_img]:max-w-full [&_img.ProseMirror-selectednode]:outline-2 [&_img.ProseMirror-selectednode]:outline-primary',
          '[&_.ProseMirror-selectednode:not(img):not(pre):not([data-node-view-wrapper])]:bg-primary/20',
          // Placeholder
          '[&_:is(p,h1,h2,h3,h4).is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_:is(p,h1,h2,h3,h4).is-editor-empty:first-child]:before:text-dimmed [&_:is(p,h1,h2,h3,h4).is-editor-empty:first-child]:before:float-left [&_:is(p,h1,h2,h3,h4).is-editor-empty:first-child]:before:h-0 [&_:is(p,h1,h2,h3,h4).is-editor-empty:first-child]:before:pointer-events-none',
          '[&_li_.is-empty]:before:content-none',
          // Tables
          '[&_table]:w-full [&_table]:border-separate [&_table]:border-spacing-0 [&_table]:rounded-md',
          '[&_th]:py-3 [&_th]:px-4 [&_th]:font-semibold [&_th]:text-sm [&_th]:text-left [&_th]:bg-muted/50 [&_th]:border-t [&_th]:border-b [&_th]:border-e [&_th]:first:border-s [&_th]:border-muted',
          '[&_th_p]:my-0 [&_th_p]:leading-5',
          '[&_td]:py-3 [&_td]:px-4 [&_td]:text-sm [&_td]:text-left [&_td]:border-b [&_td]:border-e [&_td]:first:border-s [&_td]:border-muted',
          '[&_td_p]:my-0 [&_td_p]:leading-5 [&_td_code]:text-xs/5 [&_td_ul]:my-0 [&_td_ol]:my-0 [&_td_ul]:ps-4.5 [&_td_ol]:ps-4.5 [&_td_li]:leading-6 [&_td_li]:my-0.5',
          '[&_tr:first-child_th:first-child]:rounded-tl-md [&_tr:first-child_th:last-child]:rounded-tr-md [&_tr:last-child_td:first-child]:rounded-bl-md [&_tr:last-child_td:last-child]:rounded-br-md',
          '[&_.selectedCell]:bg-primary/10 [&_.selectedCell]:ring-2 [&_.selectedCell]:ring-primary [&_.selectedCell]:ring-inset',
          // Task lists
          '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:ps-1',
          '[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-center [&_ul[data-type=taskList]_li]:ps-0',
          '[&_ul[data-type=taskList]_li_label]:inline-flex [&_ul[data-type=taskList]_li_label]:pr-2.5',
          '[&_ul[data-type=taskList]_li_label_input]:appearance-none [&_ul[data-type=taskList]_li_label_input]:size-4 [&_ul[data-type=taskList]_li_label_input]:rounded-sm [&_ul[data-type=taskList]_li_label_input]:ring [&_ul[data-type=taskList]_li_label_input]:ring-inset [&_ul[data-type=taskList]_li_label_input]:ring-accented [&_ul[data-type=taskList]_li_label_input]:bg-center',
          '[&_ul[data-type=taskList]_li_label_input:checked]:bg-primary [&_ul[data-type=taskList]_li_label_input:checked]:ring-primary [&_ul[data-type=taskList]_li_label_input:checked]:bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PHBhdGggZD0iTTIwIDZMOSAxN2wtNS01Ii8+PC9zdmc+)] dark:[&_ul[data-type=taskList]_li_label_input:checked]:bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImJsYWNrIiBzdHJva2Utd2lkdGg9IjMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PHBhdGggZD0iTTIwIDZMOSAxN2wtNS01Ii8+PC9zdmc+)]',
          '[&_ul[data-type=taskList]_li[data-checked=true]>div>p]:line-through [&_ul[data-type=taskList]_li[data-checked=true]>div>p]:opacity-50',
        ],
      },
    },
    chatMessage: {
      variants: {
        compact: {
          true: {
            content: 'text-sm leading-[1.65rem]',
          },
        },
      },
    },
    chatMessages: {
      slots: {
        indicator:
          'h-6 flex items-center gap-1 py-3 *:size-2 *:rounded-full *:bg-primary-500/30 [&>*:nth-child(1)]:animate-[bounce_1s_infinite] [&>*:nth-child(2)]:animate-[bounce_1s_0.15s_infinite] [&>*:nth-child(3)]:animate-[bounce_1s_0.3s_infinite]',
      },
    },
    prose: {
      h1: {
        slots: {
          base: 'text-xl mb-4',
        },
      },
      h2: {
        slots: {
          base: 'text-lg mt-6 mb-3 [&>a>code]:text-sm/5',
        },
      },
      h3: {
        slots: {
          base: 'text-base mt-4 mb-2 [&>a>code]:text-xs/4',
        },
      },
      h4: {
        slots: {
          base: 'text-sm mt-3 mb-1.5',
        },
      },
      p: {
        base: 'my-3 text-sm leading-[1.65rem] text-pretty',
      },
      ul: {
        base: 'list-disc ps-4 my-3 marker:text-(--ui-border-accented)',
      },
      ol: {
        base: 'list-decimal ps-4 my-3 marker:text-muted',
      },
      li: {
        base: 'my-1 ps-1.5 text-sm leading-[1.65rem] [&>ul]:my-0',
      },
      blockquote: {
        base: 'border-s-4 border-accented ps-4 italic text-sm leading-[1.65rem]',
      },
      code: {
        base: 'px-1.5 py-0.5 text-sm leading-5 font-mono font-medium rounded-md inline-block',
      },
      pre: {
        slots: {
          root: 'my-3',
          header: 'text-sm/[1.65rem]',
          filename: 'text-default text-sm/[1.65rem]',
          base: 'text-sm/[1.65rem]',
        },
      },
      th: {
        base: 'text-sm leading-[1.65rem]',
      },
      td: {
        base: 'text-sm leading-[1.65rem]',
      },
    },
  },
})
